// React Hook for Executive Coaching Protocol Integrations
// Provides coaching protocol functionality to React components

import { useCallback, useEffect, useMemo } from 'react';
import { CoachingProtocol, getCoachArchitecture } from '../lib/coachingProtocol';
import { 
  useSessionMemoryStore, 
  updateEngagementTracking,
  SessionMemory,
  CoachingContext 
} from '../lib/sessionMemoryStore';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  coachId?: string;
}

export interface CoachingProtocolHook {
  // Session opening
  generateSessionOpening: (coachId: string) => string | null;
  
  // Engagement monitoring
  checkEngagementIssues: (messages: Message[]) => {
    hasIssues: boolean;
    shouldIntervene: boolean;
    interventionMessage?: string;
  };
  
  // Blind spot detection
  checkForBlindSpots: (coachId: string, messages: Message[]) => string | null;
  
  // Boundary checking
  checkCoachBoundaries: (coachId: string, userMessage: string) => string | null;
  
  // Contextual closing
  generateContextualClosing: (
    coachId: string,
    coreInsight: string,
    nextAction?: string,
    nextReflection?: string
  ) => string;
  
  // Memory management
  updateSessionInsight: (coachId: string, insight: string, action?: string, reflection?: string) => void;
  getSessionMemory: (coachId: string) => SessionMemory;
  
  // Message tracking
  addMessageToHistory: (message: Message, coachId?: string) => void;
  
  // Session validation
  validateSessionCompletion: (
    initialClarity: number,
    finalClarity: number,
    hasActionOrReflection: boolean
  ) => boolean;
}

export const useCoachingProtocol = (sessionId: string | null): CoachingProtocolHook => {
  const {
    getSessionMemory,
    updateSessionMemory,
    getCoachingContext,
    updateCoachingContext,
    addMessageToContext,
    incrementSessionCount
  } = useSessionMemoryStore();

  // Generate session opening with memory integration
  const generateSessionOpening = useCallback((coachId: string): string | null => {
    if (!sessionId) return null;
    
    const sessionMemory = getSessionMemory(sessionId, coachId);
    return CoachingProtocol.generateSessionOpening(coachId, sessionMemory);
  }, [sessionId, getSessionMemory]);

  // Check for engagement issues and generate intervention if needed
  const checkEngagementIssues = useCallback((messages: Message[]) => {
    const messageHistory = messages.map(m => ({ text: m.text, sender: m.sender }));
    const issues = CoachingProtocol.detectEngagementIssues(messageHistory);
    
    if (issues.shouldIntervene && sessionId) {
      // Update engagement tracking for all active coaches
      const activeCoaches = [...new Set(messages.filter(m => m.coachId).map(m => m.coachId!))];
      activeCoaches.forEach(coachId => {
        updateEngagementTracking(sessionId, coachId, messageHistory);
      });
      
      // Generate intervention for primary coach (first in list or most recent)
      const primaryCoach = activeCoaches[0];
      if (primaryCoach) {
        const context = getCoachingContext(sessionId, primaryCoach);
        const interventionMessage = CoachingProtocol.generateInterventionResponse(primaryCoach, context);
        
        // Update intervention count
        updateCoachingContext(sessionId, primaryCoach, {
          interventionCount: context.interventionCount + 1
        });
        
        return {
          hasIssues: true,
          shouldIntervene: true,
          interventionMessage
        };
      }
    }
    
    return {
      hasIssues: issues.hasRepetition || issues.hasShortResponses || issues.hasFrustration,
      shouldIntervene: false
    };
  }, [sessionId, getCoachingContext, updateCoachingContext]);

  // Check for blind spots specific to coach
  const checkForBlindSpots = useCallback((coachId: string, messages: Message[]): string | null => {
    if (!sessionId) return null;
    
    const messageHistory = messages.map(m => ({ text: m.text, sender: m.sender }));
    const blindSpotResponse = CoachingProtocol.detectBlindSpots(coachId, messageHistory);
    
    if (blindSpotResponse) {
      // Track that we surfaced this blind spot
      updateCoachingContext(sessionId, coachId, {
        lastBlindSpotSurfaced: blindSpotResponse
      });
    }
    
    return blindSpotResponse;
  }, [sessionId, updateCoachingContext]);

  // Check coach boundaries and suggest alternatives
  const checkCoachBoundaries = useCallback((coachId: string, userMessage: string): string | null => {
    if (!sessionId) return null;
    
    const boundaryResponse = CoachingProtocol.checkCoachBoundaries(coachId, userMessage);
    
    if (boundaryResponse) {
      // Track boundary check
      updateCoachingContext(sessionId, coachId, {
        lastBoundaryCheck: boundaryResponse
      });
    }
    
    return boundaryResponse;
  }, [sessionId, updateCoachingContext]);

  // Generate contextual closing
  const generateContextualClosing = useCallback((
    coachId: string,
    coreInsight: string,
    nextAction?: string,
    nextReflection?: string
  ): string => {
    return CoachingProtocol.generateContextualClosing(coachId, coreInsight, nextAction, nextReflection);
  }, []);

  // Update session memory with insights and actions
  const updateSessionInsight = useCallback((
    coachId: string,
    insight: string,
    action?: string,
    reflection?: string
  ) => {
    if (!sessionId) return;
    
    updateSessionMemory(sessionId, coachId, {
      lastKeyInsight: insight,
      agreedAction: action,
      agreedReflection: reflection
    });
    
    incrementSessionCount(sessionId, coachId);
  }, [sessionId, updateSessionMemory, incrementSessionCount]);

  // Get session memory for a coach
  const getSessionMemoryForCoach = useCallback((coachId: string): SessionMemory => {
    if (!sessionId) {
      return {
        sessionCount: 0,
        coreThemes: [],
        engagementLevel: 'high',
        detectedPatterns: []
      };
    }
    
    return getSessionMemory(sessionId, coachId);
  }, [sessionId, getSessionMemory]);

  // Add message to coaching history
  const addMessageToHistory = useCallback((message: Message, coachId?: string) => {
    if (!sessionId) return;
    
    // Add to specific coach context if provided
    if (coachId) {
      addMessageToContext(sessionId, coachId, message);
    } else if (message.coachId) {
      // Add to the coach that sent the message
      addMessageToContext(sessionId, message.coachId, message);
    } else {
      // Add to all active coach contexts for user messages
      const activeCoaches = ['rajesh', 'samuel', 'priya', 'meera', 'aisha', 'arjun']; // Could be dynamic
      activeCoaches.forEach(coach => {
        addMessageToContext(sessionId, coach, message);
      });
    }
  }, [sessionId, addMessageToContext]);

  // Validate session completion
  const validateSessionCompletion = useCallback((
    initialClarity: number,
    finalClarity: number,
    hasActionOrReflection: boolean
  ): boolean => {
    return CoachingProtocol.validateSessionCompletion(
      initialClarity,
      finalClarity,
      hasActionOrReflection,
      true, // hasContextualClosing
      true  // hasContinuitySetup
    );
  }, []);

  return {
    generateSessionOpening,
    checkEngagementIssues,
    checkForBlindSpots,
    checkCoachBoundaries,
    generateContextualClosing,
    updateSessionInsight,
    getSessionMemory: getSessionMemoryForCoach,
    addMessageToHistory,
    validateSessionCompletion
  };
};

// Helper hook for coach-specific behavior
export const useCoachBehavior = (coachId: string) => {
  const architecture = useMemo(() => getCoachArchitecture(coachId), [coachId]);
  
  const isEmpathEAR = coachId === 'arjun';
  const empathEARBehavior = architecture?.specialBehaviors?.empathEAR;
  
  const shouldLimitWords = useCallback((exchangeCount: number): boolean => {
    return isEmpathEAR && !!empathEARBehavior && exchangeCount <= 3;
  }, [isEmpathEAR, empathEARBehavior]);
  
  const shouldAvoidAdvice = useCallback((): boolean => {
    return isEmpathEAR && (empathEARBehavior?.noAdvice ?? false);
  }, [isEmpathEAR, empathEARBehavior]);
  
  const shouldAvoidAnalysis = useCallback((): boolean => {
    return isEmpathEAR && (empathEARBehavior?.noAnalysis ?? false);
  }, [isEmpathEAR, empathEARBehavior]);
  
  const getTransitionRule = useCallback((): string | undefined => {
    return empathEARBehavior?.transitionRule;
  }, [empathEARBehavior]);
  
  return {
    architecture,
    isEmpathEAR,
    shouldLimitWords,
    shouldAvoidAdvice,
    shouldAvoidAnalysis,
    getTransitionRule
  };
};
